import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useUpdateTrelloInfoMutation,
  useLazyGetTrelloBoardsQuery,
  useLazyGetTrelloListsQuery,
  useDisconnectTrelloMutation,
  useLazyGetTrelloConnectionStatusQuery,
} from '../../../features/apis/intergration';
import useCurrentUser from '../../../customHook/useCurrentUser';
import ReactToastify from '../../../customHook/useReactToastify';
import { FaArrowRightLong } from 'react-icons/fa6';
import SelectSkeleton from '../SelectSkeleton';
import TrelloIcon from '../../../assets/images/trello.svg';

interface List {
  id: string;
  name: string;
}

interface Board {
  id: string;
  name: string;
}

const TrelloSelect: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [updateTrelloInfo, { isLoading }] = useUpdateTrelloInfoMutation();
  const [getBoards, { data: boardsData }] = useLazyGetTrelloBoardsQuery();
  const [getLists, { data: listsData }] = useLazyGetTrelloListsQuery();
  const [disconnectTrello] = useDisconnectTrelloMutation();
  const [getConnectionStatus, { data: connectionStatus }] =
    useLazyGetTrelloConnectionStatusQuery();
  const user = useCurrentUser();
  const [selectedBoard, setSelectedBoard] = useState<string>('');
  const [selectedList, setSelectedList] = useState<string>('');
  const [isLoadingBoards, setIsLoadingBoards] = useState(false);

  const params = new URLSearchParams(location.search);
  const companyId = params.get('companyId') || user?.companyId || '';

  useEffect(() => {
    if (companyId) {
      getConnectionStatus(companyId)
        .unwrap()
        .then((res: any) => {
          if (res?.boards?.length > 0 && !selectedBoard) {
            setSelectedBoard(res.boards[0].projectId);
            if (res.boards[0].role) {
              setSelectedList(res.boards[0].role);
            }
          }
        })
        .catch(() => {});
    }
  }, [companyId, getConnectionStatus]);

  useEffect(() => {
    if (companyId && !selectedBoard) {
      setIsLoadingBoards(true);
      getBoards(companyId)
        .unwrap()
        .then((res: any) => {
          if (res?.boards?.length > 0 && !selectedBoard) {
            setSelectedBoard(res.boards[0].id);
          }
        })
        .catch(() => {})
        .finally(() => setIsLoadingBoards(false));
    }
  }, [companyId, selectedBoard, getBoards]);

  useEffect(() => {
    if (selectedBoard && companyId) {
      getLists({ companyId, boardId: selectedBoard });
    }
  }, [selectedBoard, companyId]);

  const handleSubmit = async () => {
    if (!selectedList || !selectedBoard || !companyId) {
      ReactToastify({
        success: false,
        message: 'Board or list missing',
      });
      return;
    }

    try {
      await updateTrelloInfo({
        companyId,
        data: {
          isTrello: true,
          boardId: selectedBoard,
          listId: selectedList,
        },
      }).unwrap();

      ReactToastify({
        success: true,
        message: 'Trello info updated successfully',
      });

      navigate('/dashboard/ask-tara');
    } catch (err) {
      console.error('Trello update failed:', err);
      ReactToastify({
        success: false,
        message: 'Something went wrong while saving Trello info',
      });
    }
  };

  const handleDisconnect = async () => {
    if (!companyId) return;
    try {
      await disconnectTrello(companyId).unwrap();
      setSelectedBoard('');
      setSelectedList('');
      ReactToastify({ success: true, message: 'Trello disconnected' });
    } catch (err) {
      ReactToastify({ success: false, message: 'Failed to disconnect Trello' });
    }
  };

  return (
    <div className="relative w-full h-screen  overflow-hidden font-inter">
      <div className="flex items-center justify-center flex-col h-full">
        <div className="bg-white w-3/6  h-auto min-h-72 p-10 shadow-lg rounded-md border-[0.5px] border-[#eae8e8]">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <img src={TrelloIcon} alt="Trello" className="h-8 w-8" />
              <h1 className="text-2xl font-bold">Trello</h1>
            </div>
          </div>

          {isLoadingBoards || !boardsData ? (
            <SelectSkeleton />
          ) : (
            <>
              <div>
                <label className="mb-2 text-xs font-roboto pl-2 block">
                  Select Board
                </label>
                <select
                  value={selectedBoard}
                  onChange={(e) => {
                    setSelectedBoard(e.target.value);
                    setSelectedList('');
                  }}
                  className="w-full px-4 py-3 border-[0.5px] border-[#E2E2E2] rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-[#E2E2E2] text-sm text-gray-700 bg-white"
                >
                  {boardsData?.boards?.map((board: Board) => (
                    <option key={board.id} value={board.id}>
                      {board.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="my-2 text-xs font-roboto pl-2 block">
                  Select List
                </label>
                <select
                  value={selectedList}
                  onChange={(e) => setSelectedList(e.target.value)}
                  className="w-full px-4 py-3 border-[0.5px] border-[#E2E2E2] rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-[#E2E2E2] text-sm text-gray-700 bg-white"
                >
                  <option value="">Select a List</option>
                  {listsData?.lists?.map((list: List) => (
                    <option key={list.id} value={list.id}>
                      {list.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end items-center mt-4">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isLoading || !selectedList}
                  className="bg-gradient-to-r from-[#6E3EF5] to-[#A818D8] hover:from-[#6E3EF5]/80 hover:to-[#A818D8]/80 flex items-center justify-center p-2 font-inter rounded-md text-white gap-2 hover:scale-105 hover:shadow text-xs md:text-sm add-my-team-btn disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Saving...' : 'Submit'} <FaArrowRightLong />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrelloSelect;
